import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, RotateCcw, ClipboardPaste } from "lucide-react";

export default function WatchlistEditor() {
  const [symbols, setSymbols] = useState([]);
  const [input, setInput] = useState("");
  const [bulkInput, setBulkInput] = useState("");
  const [bulkMode, setBulkMode] = useState("replace"); // "replace" | "append"
  const [showBulk, setShowBulk] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/strategy/watchlist");
      setSymbols(r.data.symbols || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await api.patch("/strategy/watchlist", { symbols: next });
      setSymbols(r.data.symbols);
      toast.success(`Watchlist saved · ${r.data.symbols.length} symbols`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save watchlist");
    } finally {
      setSaving(false);
    }
  };

  const addSymbol = (e) => {
    e?.preventDefault?.();
    const sym = (input || "").trim().toUpperCase();
    if (!sym) return;
    if (!/^[A-Z0-9]{1,6}$/.test(sym)) {
      toast.error("Invalid symbol (1-6 letters/digits)");
      return;
    }
    if (symbols.includes(sym)) {
      toast.error(`${sym} is already on the watchlist`);
      return;
    }
    setInput("");
    save([...symbols, sym]);
  };

  const removeSymbol = (sym) => {
    if (symbols.length <= 1) {
      toast.error("Watchlist must have at least one symbol");
      return;
    }
    save(symbols.filter((s) => s !== sym));
  };

  const bulkImport = (e) => {
    e?.preventDefault?.();
    const raw = (bulkInput || "").toUpperCase();
    const parsed = Array.from(new Set(
      raw.split(/[\s,;\n\r\t]+/).map((s) => s.trim()).filter(Boolean)
    ));
    const invalid = parsed.filter((s) => !/^[A-Z0-9.\-]{1,10}$/.test(s));
    if (invalid.length) {
      toast.error(`Invalid: ${invalid.slice(0, 5).join(", ")}${invalid.length > 5 ? "…" : ""}`);
      return;
    }
    if (parsed.length === 0) {
      toast.error("No symbols found — paste tickers separated by spaces, commas or newlines");
      return;
    }
    if (parsed.length > 100) {
      toast.error(`Too many symbols (${parsed.length}) — max 100`);
      return;
    }
    const next = bulkMode === "replace"
      ? parsed
      : Array.from(new Set([...symbols, ...parsed]));
    if (next.length > 100) {
      toast.error(`Combined list is ${next.length} — over the 100 cap`);
      return;
    }
    setBulkInput("");
    setShowBulk(false);
    save(next);
  };

  const resetToDefault = async () => {
    setSaving(true);
    try {
      const r = await api.post("/strategy/watchlist/reset");
      setSymbols(r.data.symbols);
      toast.success(`Reset to default · ${r.data.symbols.length} symbols`);
    } catch (err) {
      toast.error("Could not reset");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel" data-testid="watchlist-editor">
      <div className="px-4 py-3 border-b border-[#27272A] mono text-[11px] tracking-[0.22em] flex items-center justify-between">
        <span>WATCHLIST · {symbols.length} SYMBOLS</span>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setShowBulk((v) => !v)}
            data-testid="toggle-bulk-btn"
            className="mono text-[10px] tracking-widest text-[#71717A] hover:text-white flex items-center gap-1.5"
          >
            <ClipboardPaste size={11} /> BULK PASTE
          </button>
          <button
            type="button"
            onClick={resetToDefault}
            disabled={saving}
            data-testid="reset-watchlist-btn"
            className="mono text-[10px] tracking-widest text-[#71717A] hover:text-white flex items-center gap-1.5 disabled:opacity-40"
          >
            <RotateCcw size={11} /> RESET TO DEFAULT (100)
          </button>
        </div>
      </div>

      {showBulk && (
        <form onSubmit={bulkImport} className="p-4 border-b border-[#27272A] space-y-3" data-testid="bulk-import-form">
          <div className="mono text-[10px] text-[#71717A] tracking-widest">
            PASTE TICKERS · SPACE / COMMA / NEWLINE SEPARATED · MAX 100
          </div>
          <textarea
            value={bulkInput}
            onChange={(e) => setBulkInput(e.target.value)}
            placeholder="AAPL TSLA NVDA MSFT&#10;GOOGL AMZN META&#10;..."
            rows={5}
            data-testid="bulk-import-textarea"
            className="w-full bg-[#141415] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm uppercase tracking-wider resize-y"
          />
          <div className="flex items-center gap-4 mono text-[10px] tracking-widest text-[#A1A1AA]">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="bulk-mode"
                checked={bulkMode === "replace"}
                onChange={() => setBulkMode("replace")}
                data-testid="bulk-mode-replace"
              />
              REPLACE ALL
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="bulk-mode"
                checked={bulkMode === "append"}
                onChange={() => setBulkMode("append")}
                data-testid="bulk-mode-append"
              />
              APPEND TO EXISTING
            </label>
            <button
              type="submit"
              disabled={saving || !bulkInput.trim()}
              data-testid="bulk-import-submit-btn"
              className="ml-auto bg-white text-black mono text-[10px] tracking-[0.22em] font-bold px-4 py-2 hover:bg-[#D4D4D8] disabled:opacity-40"
            >
              IMPORT
            </button>
          </div>
        </form>
      )}

      <form onSubmit={addSymbol} className="p-4 flex gap-2 border-b border-[#27272A]">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value.toUpperCase())}
          placeholder="ADD TICKER (e.g. AAPL)"
          maxLength={6}
          data-testid="watchlist-input"
          className="flex-1 bg-[#141415] border border-[#27272A] focus:border-[#52525B] px-3 py-2 mono text-sm uppercase tracking-wider"
        />
        <button
          type="submit"
          disabled={saving || !input}
          data-testid="watchlist-add-btn"
          className="bg-white text-black mono text-[10px] tracking-[0.22em] font-bold px-4 py-2 hover:bg-[#D4D4D8] disabled:opacity-40 flex items-center gap-1.5"
        >
          <Plus size={12} /> ADD
        </button>
      </form>

      <div className="p-4">
        {loading ? (
          <div className="text-center text-[#71717A] mono text-xs py-4">LOADING...</div>
        ) : symbols.length === 0 ? (
          <div className="text-center text-[#71717A] mono text-xs py-4">NO SYMBOLS — ADD ONE OR RESET TO DEFAULT</div>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="watchlist-chips">
            {symbols.map((s) => (
              <div
                key={s}
                data-testid={`chip-${s}`}
                className="inline-flex items-center gap-1.5 bg-[#141415] border border-[#27272A] mono text-xs font-bold pl-2.5 pr-1 py-1 hover:border-[#52525B] group"
              >
                <span>{s}</span>
                <button
                  type="button"
                  onClick={() => removeSymbol(s)}
                  disabled={saving}
                  data-testid={`chip-remove-${s}`}
                  className="text-[#71717A] hover:text-[#FF3B30] p-0.5 disabled:opacity-30"
                  aria-label={`Remove ${s}`}
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-4 py-2 mono text-[10px] text-[#71717A] border-t border-[#27272A]">
        Bot scans these symbols every 15 min · max 100
      </div>
    </div>
  );
}
